import { lazy } from 'react'
import type { TutorialDef } from './types'
import * as needsYourAttention from './needs-your-attention/meta'
import * as home from './home/meta'

/* Every page tutorial. Only the small meta modules are imported here; each
   film's code is fetched the first time a player renders it (or preload()). */

const loadNeedsYourAttention = () => import('./needs-your-attention/Film')
const loadHome = () => import('./home/Film')

export const TUTORIALS: TutorialDef[] = [
  {
    id: 'needs-your-attention',
    version: 1,
    title: 'Needs your attention',
    routes: [/^\/queue(\/|$)/],
    width: needsYourAttention.WIDTH,
    height: needsYourAttention.HEIGHT,
    fps: needsYourAttention.FPS,
    durationInFrames: needsYourAttention.DURATION,
    posterFrame: needsYourAttention.POSTER,
    chapters: needsYourAttention.CHAPTERS,
    mini: needsYourAttention.MINI,
    Composition: lazy(loadNeedsYourAttention),
    preload: loadNeedsYourAttention,
    explanation: {
      summary:
        'Needs your attention is one list of everything waiting on you — customer replies, calls tenfour handled, questions from tenfour, and invoices that need your OK. Open an item and the evidence sits beside it, so you can decide and move on.',
      steps: [
        {
          title: 'Pick an item',
          body: "Most urgent first: replies and questions from tenfour sit on top. A dot marks what you haven't opened. Use All, Jobs, and Activity to narrow the list.",
        },
        {
          title: 'See what happened',
          body: 'The pane shows the conversation, the call recording with its transcript, and what tenfour already did.',
        },
        {
          title: 'Decide in one step',
          body: "Send the drafted reply, answer tenfour's question, approve an invoice, or dismiss it. The item clears and the next one opens.",
        },
      ],
    },
  },
  {
    id: 'home',
    version: 1,
    title: 'Home',
    routes: [/^\/home\/?$/],
    width: home.WIDTH,
    height: home.HEIGHT,
    fps: home.FPS,
    durationInFrames: home.DURATION,
    posterFrame: home.POSTER,
    chapters: home.CHAPTERS,
    Composition: lazy(loadHome),
    preload: loadHome,
    explanation: {
      summary:
        'Home is where your day starts: what needs you first, what tenfour handled for you, and what came in.',
      // In the film's order: the banner, For you, Activity, Paid this week.
      steps: [
        {
          title: 'The setup banner',
          body: 'Until setup is finished, the banner shows your next step. Every step lives in Help under Get set up.',
        },
        {
          title: 'For you',
          body: 'The few things waiting on you, most important first. Open one to decide in Needs your attention.',
        },
        {
          title: 'Activity',
          body: 'Calls and conversations tenfour handled, including live calls. Open a call to hear the recording and read the transcript.',
        },
        {
          title: 'Paid this week',
          body: 'Money received in the last seven days.',
        },
      ],
    },
  },
]

/** The tutorial that belongs to a page, if any. */
export function tutorialForPath(pathname: string): TutorialDef | undefined {
  return TUTORIALS.find((tutorial) => tutorial.routes.some((route) => route.test(pathname)))
}

export function tutorialById(id: string): TutorialDef | undefined {
  return TUTORIALS.find((tutorial) => tutorial.id === id)
}

/** Display length for cards and captions ('30 sec'), always derived from the
    film's frames and floored like the player's clock, so the two agree. */
export function tutorialLength(tutorial: TutorialDef): string {
  const seconds = Math.floor(tutorial.durationInFrames / tutorial.fps)
  return seconds < 60 ? `${Math.max(1, seconds)} sec` : `${Math.floor(seconds / 60)} min`
}
